<div class="action-handler">
  <div class="action-handler__container">
    <!-- Logo -->
    <div class="action-handler__logo">
      <img [src]="logoPath" alt="Tandem" class="action-handler__logo-img">
    </div>

    <!-- Title -->
    <h1 class="action-handler__title">{{ pageTitle }}</h1>

    <!-- Loading State -->
    <div *ngIf="state.isLoading && mode === 'verifyEmail'" class="action-handler__loading">
      <div class="action-handler__spinner"></div>
      <p class="action-handler__loading-text">Please wait while we verify your email...</p>
    </div>

    <!-- Error State -->
    <div *ngIf="state.errorMessage" class="action-handler__error">
      <div class="action-handler__error-icon">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
        </svg>
      </div>
      <p class="action-handler__error-text">{{ state.errorMessage }}</p>
      <button class="action-handler__btn action-handler__btn--primary" (click)="goToLogin()">
        Go to Login
      </button>
    </div>

    <!-- Success State: Email Verified -->
    <div *ngIf="state.isSuccess && mode === 'verifyEmail'" class="action-handler__success">
      <div class="action-handler__success-icon">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
      </div>
      <p class="action-handler__success-text">
        Your email has been verified successfully! You can now close this tab and return to the app.
      </p>
    </div>

    <!-- Success State: Password Reset -->
    <div *ngIf="state.isSuccess && mode === 'resetPassword'" class="action-handler__success">
      <div class="action-handler__success-icon">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
      </div>
      <p class="action-handler__success-text">
        Your password has been reset successfully! You can now sign in with your new password.
      </p>
      <button class="action-handler__btn action-handler__btn--primary" (click)="goToLogin()">
        Sign In to {{ originDomainName }}
      </button>
    </div>

    <!-- Password Reset Form -->
    <div *ngIf="mode === 'resetPassword' && !state.isSuccess && !state.errorMessage && !state.isLoading" class="action-handler__form-container">
      <p class="action-handler__subtitle">Enter your new password below.</p>
      
      <form [formGroup]="newPasswordForm" (ngSubmit)="submitNewPassword()" class="action-handler__form">
        <div class="action-handler__field">
          <label for="newPassword" class="action-handler__label">New Password</label>
          <div class="action-handler__input-wrapper">
            <input 
              [type]="showPassword ? 'text' : 'password'"
              id="newPassword"
              formControlName="newPassword"
              class="action-handler__input"
              placeholder="Enter your new password"
              autocomplete="new-password"
            >
            <button 
              type="button" 
              class="action-handler__toggle-password"
              (click)="togglePasswordVisibility()"
              tabindex="-1"
            >
              <svg *ngIf="!showPassword" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.64 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.64 0-8.573-3.007-9.963-7.178Z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
              </svg>
              <svg *ngIf="showPassword" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
              </svg>
            </button>
          </div>
          
          <!-- Password Requirements -->
          <div class="action-handler__requirements">
            <p class="action-handler__requirements-title">Password must contain:</p>
            <ul class="action-handler__requirements-list">
              <li [class.valid]="hasMinLength">At least 8 characters</li>
              <li [class.valid]="hasUppercase">One uppercase letter</li>
              <li [class.valid]="hasSpecialChar">One special character (!@#$%^&*)</li>
            </ul>
          </div>
        </div>

        <button 
          type="submit" 
          class="action-handler__btn action-handler__btn--primary"
          [disabled]="newPasswordForm.invalid || state.isLoading"
        >
          <span *ngIf="!state.isLoading">Reset Password</span>
          <span *ngIf="state.isLoading" class="action-handler__btn-loading">
            <div class="action-handler__spinner action-handler__spinner--small"></div>
            Resetting...
          </span>
        </button>
      </form>
    </div>

    <!-- Footer -->
    <div class="action-handler__footer">
      <p>Powered by <strong>Tandem</strong></p>
    </div>
  </div>
</div>

